import { useEffect } from 'react';
import { useMachine } from '@xstate/react';
import {
  ActionFunction,
  ActionTypes,
  assign,
  Machine,
  MachineOptions,
  ErrorPlatformEvent,
  EventObject,
} from 'xstate';
import gql from 'graphql-tag';
import { MutationTuple } from '@apollo/react-hooks';
import * as Sentry from '@sentry/browser';

import { useCurrentAccountId } from '@zapier/identity';

import { PaymentProcessor } from '@zapier/payment/src/types';
import {
  createSetupIntentMutation as createSetupIntentMutationType,
  createSetupIntentMutation_createSetupIntent as SetupIntent,
  createSetupIntentMutationVariables,
} from '@zapier/payment/src/graphql-types';
import { timeoutAfter } from '@zapier/payment/src/utils/timeoutAfter';
import { useApolloInjection } from '@zapier/payment/src/contexts/ApolloInjection';

type Context = {
  setupIntent?: SetupIntent;
  error?: Error;
};

type Schema = {
  states: {
    boot: {};
    idle: {};
    setup: {};
    setupError: {};
    waitForInput: {};
    savePaymentSource: {};
    errorSaving: {};
    finished: {};
  };
};

type Event =
  | { type: 'ready' }
  | { type: 'startSetup' }
  | { type: 'retry' }
  | { type: 'submit' }
  | { type: 'cancel' }
  | (ErrorPlatformEvent & { type: 'error.platform.createSetupIntent' })
  | { type: `error.platform.savePaymentSource`; data: 'validation_error' };

type SentryActionFunction = (
  transactionName: string
) => ActionFunction<Context, Event>;

const isErrorPlatformEvent = (
  event: EventObject
): event is ErrorPlatformEvent =>
  event.type.startsWith(ActionTypes.ErrorPlatform);

const logToSentry: SentryActionFunction = (transactionName: string) => {
  return (context: Context, event: Event) => {
    if (!isErrorPlatformEvent(event)) return;
    Sentry.captureException(event.data, scope => {
      scope.setTag('package', 'payment');
      scope.setTransactionName(transactionName);
      scope.setExtra('error', event.data);
      if ('graphQLErrors' in event.data) {
        scope.setExtra('GQL errors', JSON.stringify(event.data.graphQLErrors));
      }
      if ('networkError' in event.data) {
        scope.setExtra('Network error', event.data.networkError);
      }
      if (context.setupIntent?.setupIntentId) {
        scope.setContext('Setup Intent', {
          setupIntentId: context.setupIntent.setupIntentId,
        });
      }
      return scope;
    });
  };
};

const setupIntentMachine = Machine<Context, Schema, Event>(
  {
    initial: 'boot',
    context: {
      setupIntent: undefined,
      error: undefined,
    },
    states: {
      boot: {
        on: {
          ready: 'idle',
        },
      },
      idle: {
        on: {
          startSetup: 'setup',
        },
      },
      setup: {
        entry: ['clearErrors'],
        // @see https://github.com/statelyai/xstate/issues/1010
        // @ts-ignore - This is a known problem on this version of XState. An update to XState will fix it.
        invoke: {
          src: 'createSetupIntent',
          onDone: {
            target: 'waitForInput',
            actions: [
              assign({
                setupIntent: (_context, event) => event.data,
              }),
            ],
          },
          onError: {
            target: 'setupError',
            actions: [
              assign({
                error: (_context, event) => event.data,
              }),
            ],
          },
        },
      },
      setupError: {
        entry: [logToSentry('setupIntent.create'), 'destroyHostedFields'],
        on: {
          retry: 'setup',
          cancel: 'finished',
        },
      },
      waitForInput: {
        entry: ['initializeHostedFields'],
        on: {
          submit: 'savePaymentSource',
          cancel: 'finished',
        },
      },
      savePaymentSource: {
        // @see https://github.com/statelyai/xstate/issues/1010
        // @ts-ignore - This is a known problem on this version of XState. An update to XState will fix it.
        invoke: {
          src: 'savePaymentSource',
          onDone: 'finished',
          onError: [
            {
              target: 'waitForInput',
              cond: 'hasValidationErrors',
            },
            {
              target: 'errorSaving',
              actions: [
                assign({
                  error: (_context, event) => event.data,
                }),
              ],
            },
          ],
        },
      },
      errorSaving: {
        entry: [logToSentry('paymentSource.save'), 'destroyHostedFields'],
        on: {
          retry: 'setup',
          cancel: 'finished',
        },
      },
      finished: {
        type: 'final',
      },
    },
  },
  {
    actions: {
      clearErrors: assign(context => ({ ...context, error: undefined })),
    },
    guards: {
      hasValidationErrors: (_context, event) =>
        event.type === 'error.platform.savePaymentSource' &&
        event.data === 'validation_error',
    },
  }
);

export const createSetupIntentMutation = gql`
  mutation createSetupIntentMutation(
    $accountId: Int!
    $processor: PaymentSourceBackend
  ) {
    createSetupIntent(accountId: $accountId, processor: $processor) {
      setupIntentId
      clientSecret
      processor
    }
  }
`;

export const useSetupIntentMachine = (
  processor: PaymentProcessor,
  options: Partial<MachineOptions<Context, Event>> = { services: {} }
) => {
  const { useMutation } = useApolloInjection();
  const accountId = useCurrentAccountId();
  const [createSetupIntent] = useMutation(createSetupIntentMutation, {
    variables: { accountId, processor },
  }) as MutationTuple<
    createSetupIntentMutationType,
    createSetupIntentMutationVariables
  >;

  const [state, send, interpreter] = useMachine<Context, Event>(
    setupIntentMachine,
    {
      ...options,
      services: {
        ...options.services,
        createSetupIntent() {
          return timeoutAfter(
            10000,
            `This page didn't load correctly. Try again or choose a different payment method.`,
            createSetupIntent().then(({ data, errors }) => {
              if (errors) return Promise.reject(errors);
              return data?.createSetupIntent;
            })
          );
        },
      },
    }
  );

  /**
   * Wait for dependencies of this machine to be loaded before transitioning to the "ready" state
   */
  useEffect(() => {
    if (accountId) {
      send('ready');
    }
  }, [accountId, send]);

  return [state, send, interpreter] as const;
};
